import { type JSX, useCallback, useMemo } from 'react';
import { unreachable } from '@chainflip/utils/assertion';
import { toISODateString } from '@chainflip/utils/date';
import { isNotNullish } from '@chainflip/utils/guard';
import { abbreviate } from '@chainflip/utils/string';
import { UTCDate } from '@date-fns/utc';
import { Link } from '@/shared/components/atoms/Link';
import Pill, { type PillColor } from '@/shared/components/flip-ui-kit/Pill';
import { ChainLogo } from '@/shared/components/logo/ChainLogo';
import AgeWithTooltip from '@/shared/components/molecules/AgeWithTooltip';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import ViewEventButton from '@/shared/components/ViewEventButton';
import { processorClient } from '@/shared/graphql/client';
import {
  type GetDelegationActivityQuery,
  type DelegationActivityType,
} from '@/shared/graphql/generated/explorer/graphql';
import useEvmScAccount from '@/shared/hooks/useEvmScAccount';
import useRemotePagination from '@/shared/hooks/useRemotePagination';
import { triggerCsvDownload } from '@/shared/utils/export';
import FlipAmount from '@/shared/utils/FlipAmount';
import { etherscanUrl } from '@/shared/utils/helpers';
import TokenAmount, { type TokenCast } from '@/shared/utils/TokenAmount';
import { getDelegationActivityQuery } from '../../../graphql/explorer/delegatorQueries';

type DelegationActivity = NonNullable<
  GetDelegationActivityQuery['allDelegationActivities']
>['edges'][number]['node'];
const delegationActivityCastFields = ['amount'] as const;
type CastDelegationActivity = TokenCast<
  DelegationActivity,
  (typeof delegationActivityCastFields)[number]
>;

export const getEventLabel = (
  type: DelegationActivityType,
  amount: TokenAmount,
): { text: string; color: PillColor } => {
  switch (type) {
    case 'DELEGATE':
      return { text: 'Delegate', color: 'blue' as const };
    case 'UNDELEGATE':
      return { text: 'Undelegate', color: 'red' as const };
    case 'REWARD':
      return { text: 'Reward', color: 'green' as const };
    case 'SLASH':
      return { text: 'Slash', color: 'yellow' as const };
    case 'BID_CHANGE':
      if (amount.value.isNegative()) {
        return { text: 'Decrease Delegation', color: 'red' as const };
      }
      return { text: 'Increase Delegation', color: 'blue' };
    default:
      return unreachable(type);
  }
};

const DelegationActivityEventBadge = ({
  eventType,
  amount,
}: {
  eventType: DelegationActivityType;
  amount: TokenAmount;
}) => <Pill {...getEventLabel(eventType, amount)} maxWidth={false} />;

const DelegateActivityTableRow = ({
  activity: { amount, txHash, event, type },
}: {
  activity: CastDelegationActivity;
}) => (
  <tr>
    <td>
      <DelegationActivityEventBadge eventType={type} amount={amount} />
    </td>
    <td>
      <FlipLabel amount={amount} />
    </td>
    <td>
      {txHash ? (
        <span className="flex items-center space-x-0.5">
          <div className="flex h-6 w-6 items-center justify-center">
            <ChainLogo chain="Ethereum" size="medium" />
          </div>

          <Link
            href={etherscanUrl('tx', txHash)}
            target="_blank"
            underline
            noPropagate
            color="white"
          >
            <span className="whitespace-nowrap">{abbreviate(txHash)}</span>
          </Link>
        </span>
      ) : (
        'N/A'
      )}
    </td>
    <td className="whitespace-nowrap">
      <ViewEventButton event={event} />
    </td>
    <td className="whitespace-nowrap text-right text-12 text-cf-light-2">
      <AgeWithTooltip timestamp={event.block.timestamp} />
    </td>
  </tr>
);

export const DelegateActivityTable = (): JSX.Element => {
  const { idSs58: delegatorIdSs58 } = useEvmScAccount();

  const {
    data: delegationActivityEventsPaginated,
    isLoading: delegationActivityLoading,
    error,
    paginationControls,
  } = useRemotePagination(getDelegationActivityQuery, {
    variables: { delegationIdSs58: delegatorIdSs58 },
    enabled: isNotNullish(delegatorIdSs58),
    useQueryParam: true,
    totalCount: (data) => data.allDelegationActivities?.totalCount ?? 0,
  });

  const optimizedRows = useMemo(
    () =>
      FlipAmount.castObject(
        delegationActivityEventsPaginated,
        delegationActivityCastFields,
      )?.allDelegationActivities?.edges.map(({ node: activity }) => (
        <DelegateActivityTableRow key={activity.id} activity={activity} />
      )) ?? [],
    [delegationActivityEventsPaginated],
  );

  const exportCsv = useCallback(async () => {
    if (!delegatorIdSs58) return;

    const PAGE_SIZE = 500;
    let offset = 0;
    let allActivities: DelegationActivity[] = [];
    let totalCount = 0;

    // batch fetch to avoid timeouts
    do {
      // eslint-disable-next-line no-await-in-loop
      const data = await processorClient.request(getDelegationActivityQuery, {
        delegationIdSs58: delegatorIdSs58,
        first: PAGE_SIZE,
        offset,
      });

      const edges = data.allDelegationActivities?.edges ?? [];
      totalCount = data.allDelegationActivities?.totalCount ?? 0;
      allActivities = allActivities.concat(edges.map((edge) => edge.node));
      offset += PAGE_SIZE;
    } while (offset < totalCount);

    const rows = allActivities.map((activity) => {
      const amount = TokenAmount.fromAsset(activity.amount, 'Flip');
      const { text: typeLabel } = getEventLabel(activity.type, amount);
      const { timestamp } = activity.event.block;

      return [
        typeLabel,
        amount.toFixed(),
        isNotNullish(activity.valueUsd) ? Number(activity.valueUsd).toFixed(2) : '',
        activity.txHash ?? '',
        timestamp,
      ];
    });

    const headers = ['Type', 'Amount (FLIP)', 'USD Value', 'Transaction', 'Timestamp'];

    const today = toISODateString(new UTCDate());
    triggerCsvDownload(`${delegatorIdSs58}-delegation-activity-${today}.csv`, [headers, ...rows]);
  }, [delegatorIdSs58]);

  if (error) throw error;
  return (
    <TableV2
      className="rounded-t-none"
      columns={['Type', 'Amount', 'Transaction', 'Event', { alignment: 'right', name: 'Age' }]}
      rows={optimizedRows}
      isLoading={delegationActivityLoading}
      paginationControls={paginationControls}
      exportCsv={delegatorIdSs58 ? exportCsv : undefined}
    />
  );
};
